import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { usePageTitle, useStatoInSolaLettura } from '../../../../layouts/PageHeaderExtra';
import { segnalaMancanti } from '../../../../components/Util/campiMancanti';

/*
 * Scheda della tessera (03/10/2026): «Modifica tessera» ed «Elimina tessera» erano icone sulla riga
 * dell'Elenco Tesserati, ora stanno qui. Non c'è un'API che legga una tessera per id: la riga arriva
 * dalla lista (navigate state); senza (link aperto a mano, ricarica della pagina) si torna alla lista.
 */

const LIST_URL = '/struttura/tesserati/list';

const soloData = (valore) => (valore ? String(valore).substring(0, 10) : '');

const formatDate = (valore) => {
	const iso = soloData(valore);
	if (!iso) return '—';
	const [anno, mese, giorno] = iso.split('-');
	if (!anno || !mese || !giorno) return iso;
	return `${giorno}/${mese}/${anno}`;
};

const oggiISO = () => {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Attiva se oggi cade dentro la validità, come nella lista
const tesseraAttiva = (t) => {
	const oggi = oggiISO();
	if (t.dateEnd && t.dateEnd < oggi) return false;
	if (t.dateStart && t.dateStart > oggi) return false;
	return true;
};

const getToken = () => {
	const dettagli = sessionStorage.getItem('userDetails');
	return dettagli ? JSON.parse(dettagli).idToken : null;
};

export const SchedaTessera = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const iniziale = location.state?.tessera || null;
	// Una tessera archiviata è un record chiuso: non si modifica e non si rielimina
	const inArchivio = !!location.state?.inArchivio;

	const [tessera, setTessera] = useState(iniziale);
	const [dateStart, setDateStart] = useState(iniziale?.dateStart || '');
	const [dateEnd, setDateEnd] = useState(iniziale?.dateEnd || '');
	const [loadingShowed, setLoadingShowed] = useState(false);
	const [errore, setErrore] = useState('');
	const [confermaElimina, setConfermaElimina] = useState(false);
	const [salvato, setSalvato] = useState(false);

	usePageTitle(tessera?.name);
	const attiva = tessera ? tesseraAttiva(tessera) : false;
	useStatoInSolaLettura(tessera ? (inArchivio ? 'Archiviata' : attiva ? 'Attivo' : 'Non attivo') : '', inArchivio ? 'bozza' : attiva ? 'attivo' : 'non-attivo');

	if (!tessera) return <Navigate to={LIST_URL} replace />;

	const apriPlayer = () => {
		const percorso = tessera.userType === 'JUNIOR' ? '/struttura/junior/junior_profile/' : '/struttura/clienti/player_profile/';
		navigate(percorso + tessera.id);
	};

	const salva = async () => {
		const mancanti = [];
		if (!dateEnd) mancanti.push({ campo: 'tessera-fine-validita', messaggio: 'Manca la data di fine validità' });
		else if (dateStart && dateStart > dateEnd) mancanti.push({ campo: 'tessera-inizio-validita', messaggio: 'La data di inizio validità non può essere successiva alla data di fine validità' });
		if (segnalaMancanti(mancanti) > 0) return;
		const token = getToken();
		if (!token) return;
		setLoadingShowed(true);
		const risultato = await new MyApiClient().updatePlayerLicense(token, tessera.licenseId, tessera.id, {
			...(dateStart && { dateStart }),
			expiration: dateEnd,
		});
		setLoadingShowed(false);
		if (risultato && risultato.error) {
			setErrore('Si è verificato un errore durante il salvataggio: ' + risultato.error);
			return;
		}
		setTessera({ ...tessera, dateStart, dateEnd });
		setSalvato(true);
		setTimeout(() => setSalvato(false), 2500);
	};

	const elimina = async () => {
		setConfermaElimina(false);
		const token = getToken();
		if (!token) return;
		setLoadingShowed(true);
		const risultato = await new MyApiClient().deletePlayerLicense(token, tessera.licenseId, tessera.id);
		setLoadingShowed(false);
		if (risultato && risultato.error) {
			setErrore("Si è verificato un errore durante l'eliminazione: " + risultato.error);
			return;
		}
		navigate(LIST_URL);
	};

	const modificabile = !!tessera.licenseId && !inArchivio;

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status"><span className="sr-only">Loading...</span></Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={!!errore} onHide={() => setErrore('')} centered>
				<Modal.Header closeButton><Modal.Title>Errore durante l'operazione</Modal.Title></Modal.Header>
				<Modal.Body>{errore}</Modal.Body>
			</Modal>

			<Modal className="fade" show={confermaElimina} onHide={() => setConfermaElimina(false)} centered>
				<Modal.Header closeButton><Modal.Title>Eliminare la tessera?</Modal.Title></Modal.Header>
				<Modal.Body>
					Eliminare la tessera di «{tessera.name}» dal registro?
					<br /><br />
					Se è stata inserita da meno di 7 giorni viene eliminata, perché si tratta di una correzione.
					Altrimenti viene <b>archiviata</b>: per obbligo di conservazione decennale il record resta consultabile in Archivio.
				</Modal.Body>
				<Modal.Footer className="justify-content-center sid-footer-conferma">
					<Button className="btn btn-light btn-add" onClick={() => setConfermaElimina(false)}>Annulla</Button>
					<Button variant="danger" onClick={elimina}>Elimina</Button>
				</Modal.Footer>
			</Modal>

			<Card>
				<Card.Body className="sid-card-body--spaced">
					<h5 className="sid-form-section">Tessera</h5>
					<Row className="g-4">
						<Col md={6}>
							<Form.Label>N. tessera</Form.Label>
							<div className="sid-readonly-value">{tessera.licenseNumber || '—'}</div>
						</Col>
						<Col md={6}>
							<Form.Label>Tesserato</Form.Label>
							<div className="sid-readonly-value">{tessera.name || '—'}</div>
						</Col>
						<Col md={6}>
							<Form.Label>Organismo</Form.Label>
							<div className="sid-readonly-value">{tessera.federationCode || '—'}</div>
						</Col>
						<Col md={6}>
							<Form.Label>Sport</Form.Label>
							<div className="sid-readonly-value">{tessera.sportLabel || '—'}</div>
						</Col>
					</Row>

					<h5 className="sid-form-section mt-4">Validità</h5>
					<Row className="g-4">
						<Col md={6}>
							<Form.Label htmlFor="tessera-inizio-validita">Inizio validità</Form.Label>
							{modificabile
							? <Form.Control id="tessera-inizio-validita" type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
								: <div className="sid-readonly-value">{formatDate(tessera.dateStart)}</div>}
						</Col>
						<Col md={6}>
							<Form.Label htmlFor="tessera-fine-validita">Fine validità{modificabile ? ' *' : ''}</Form.Label>
							{modificabile
							? <Form.Control id="tessera-fine-validita" type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />
								: <div className="sid-readonly-value">{formatDate(tessera.dateEnd)}</div>}
						</Col>
					</Row>
				</Card.Body>
			</Card>

			<div className="d-flex justify-content-between align-items-center gap-4 mt-4">
				{modificabile
					? <button type="button" className="btn btn-danger light" onClick={() => setConfermaElimina(true)}>Elimina tessera</button>
					: <span />}
				<div className="d-flex gap-4">
					<button type="button" className="btn btn-dark light" onClick={apriPlayer}>Visualizza player</button>
					{modificabile && <Button className="btn btn-primary" onClick={salva}>Salva</Button>}
				</div>
			</div>

			{salvato && <div className="sid-toast-salvato" role="status" style={{ position: 'fixed', left: 24, bottom: 24, zIndex: 2000, background: '#111827', color: '#fff', padding: '10px 16px', borderRadius: 8 }}>Modifica salvata</div>}
		</>
	);
};

export default SchedaTessera;
